import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closePreview } from "../../redux/previewSlice";
import { showSnackbar } from "../../redux/snackbarSlice";
import PreviewAppBar from "./components/PreviewAppBar";
import ExportFormatPopup from "./components/ExportFormatPopup";
import DocumentHeader from "../Documents/components/DocumentHeader";
import RevisionTable from "../Documents/components/RevisionTable";
import TrainingLogTable from "../Documents/components/TrainingLogTable";
import BOMTable from "../Documents/components/BOMTable";
import RenderRevisionNodes from "../Documents/components/RenderRevisionNodes";
import usePreviewData from "./hooks/usePreviewData";
import useCurrentRevisionData from "../Documents/hooks/useCurrentRevisionData";
import InlineLoader from "../../common/InlineLoader";
import { useLocation } from "react-router-dom";
import { getBomRows } from "../../util/AppUtils";

/**
 * Preview Component
 * Preview modal that displays procedure revision information
 * Works with Redux global state - can use cached data or fetch fresh
 * No action buttons (Submit/Reject/Approve/Release)
 */
function Preview() {
  const HEADING_TO_TABLE_GAP = "14px";
  const SECTION_TO_SECTION_GAP = "20px";

  const dispatch = useDispatch();
  const location = useLocation();

  const { fromAuditTrail, cancel_screen_data } = location?.state || {}

  // Get preview state from Redux
  const previewState = useSelector((state) => state.preview);
  const {
    partId,
    revisionId,
    useCache,
    cachedRevisionData,
    cachedTrainingLogs,
    cachedBomTable,
    cachedCurrentRevision,
    cachedAllNodesData,
    draftBomItems, // BOM items from draft step
    draftRevision, // Draft revision data from EditProcedureRevision
  } = previewState;

  // Fetch fresh data only when not using cache
  const {
    revisionData: fetchedRevisionData,
    trainingLogs: fetchedTrainingLogs,
    bomTable: fetchedBomTable,
    loading,
  } = usePreviewData(
    useCache ? null : partId, // Don't fetch if using cache
    useCache ? null : revisionId
  );

  // Determine which data to use: cached or fetched
  let revisionData = useCache ? cachedRevisionData : fetchedRevisionData;
  let trainingLogs = useCache ? cachedTrainingLogs : fetchedTrainingLogs;
  let bomTable = useCache ? cachedBomTable : fetchedBomTable;
  let renderRevisionNodesData = useCache ? cachedAllNodesData : null

  if(fromAuditTrail){
    revisionData = cancel_screen_data?.present_revisions
    trainingLogs = cancel_screen_data?.present_training_logs
    bomTable = getBomRows(cancel_screen_data?.data)
    renderRevisionNodesData = cancel_screen_data?.data
    // trainingLogs = current_
  }


  // Merge draft BOM items into main BOM table
  if (draftBomItems && draftBomItems.length > 0) {
    // Create a copy of the BOM table to avoid mutations
    const mergedBomTable = [...(bomTable || [])];

    // Process each draft BOM item
    draftBomItems.forEach((draftItem) => {
      // Find if this part already exists in the BOM table
      const existingItemIndex = mergedBomTable.findIndex(
        (item) =>
          item.part_ref === draftItem.partId || item.partId === draftItem.partId
      );

      if (existingItemIndex !== -1) {
        // Item exists - add quantities
        const existingItem = mergedBomTable[existingItemIndex];
        const existingQty = parseFloat(existingItem.qty) || 0;
        const draftQty = parseFloat(draftItem.qty) || 0;

        mergedBomTable[existingItemIndex] = {
          ...existingItem,
          qty: existingQty + draftQty,
          // Mark as modified for visual indication
          isModified: true,
        };
      } else {
        // New item - add to table
        mergedBomTable.push({
          part_ref: draftItem.partId,
          partId: draftItem.partId,
          part_number: draftItem.partNumber,
          part_description: draftItem.description, // BOM format uses part_description
          type_name: draftItem.type, // BOM format uses type_name
          orders: draftItem.type_details?.orders || 999999, // Extract orders to root level for BOM format
          qty: draftItem.qty,
          unit_name: draftItem.unit_name,
          // Mark as new for visual indication
          isNew: true,
        });
      }
    });

    // Update bomTable with merged data
    bomTable = mergedBomTable;
  }

  // Merge draft revision data into revision table
  if (draftRevision && revisionData && revisionData.length > 0) {
    // Create a copy of the revision data to avoid mutations
    const mergedRevisionData = [...revisionData];

    // Find the current revision and update it with draft data
    const currentRevisionIndex = mergedRevisionData.findIndex(
      (rev) => rev.procedure_revision_id === revisionId
    );

    if (currentRevisionIndex !== -1) {
      // Merge draft revision data into the current revision
      mergedRevisionData[currentRevisionIndex] = {
        ...mergedRevisionData[currentRevisionIndex],
        ...draftRevision, // This will override reference and revision_details
        // Mark as modified for visual indication
        isModified: true,
      };
    }

    // Update revisionData with merged data
    revisionData = mergedRevisionData;
  }

  // Get current revision and status using the same hook as Document.js
  // This ensures consistent status calculation including pending approvals check
  // The hook will find the revision from revisionData (which is either cached or fetched)
  const { currentRevision: hookCurrentRevision, currentStatus: calculatedStatus } = useCurrentRevisionData(
    revisionData || [],
    revisionId
  );

  // Use cached revision if available (it might have more complete data), otherwise use hook's result
  const finalCurrentRevision = useCache && cachedCurrentRevision
    ? cachedCurrentRevision
    : hookCurrentRevision;

  // Always use the hook's calculated status (it handles pending approvals correctly)
  // If hook didn't calculate status (e.g., no revisionData), fallback to direct lookup
  let currentStatus = calculatedStatus;
  if (!currentStatus && finalCurrentRevision) {
    // Fallback: calculate status manually if hook didn't return one
    const latestStatusChange = finalCurrentRevision?.status_changes?.find(
      (sc) => sc.is_latest
    );
    const statusValue = latestStatusChange?.status_changed_to || null;
    const hasPendingApprovals = finalCurrentRevision.required_by?.some(
      (req) => req.purpose?.toLowerCase() === "approve" && req.is_open
    );

    // Apply same logic as useCurrentRevisionData hook
    if (
      statusValue &&
      statusValue.toLowerCase() === "approved" &&
      hasPendingApprovals
    ) {
      currentStatus = "Submitted For Approval";
    } else {
      currentStatus = statusValue;
    }
  }

  // Ensure "Released" and "Archived" statuses display with capital R and A
  if (currentStatus) {
    if (currentStatus.toLowerCase() === "released") {
      currentStatus = "Released";
    } else if (currentStatus.toLowerCase() === "archived") {
      currentStatus = "Archived";
    }
  }

  // Preview is always read-only (checkboxes hidden)
  const isCheckboxEnabled = false;

  // Handle close preview
  const handleClose = () => {
    dispatch(closePreview());
  };


  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Export popup state
  const [isExportPopupOpen, setIsExportPopupOpen] = useState(false);
  const [arePreviewNodesLoaded, setArePreviewNodesLoaded] = useState(
    useCache
      ? cachedAllNodesData !== null && cachedAllNodesData !== undefined
      : false,
  );

  // Additional information container state (to close it before export)
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  // Ref for content container (for PDF/mHTML export)
  const contentRef = useRef(null);

  // Get app setup data for PDF header (with safe default)
  const { items: appSetupItems } = useSelector((state) => state.appSetup || {});

  // Check if any data is loading
  const isAnyLoading =
    !useCache &&
    (loading.revisionData || loading.trainingLogs || loading.bomTable);

  useEffect(() => {
    const hasCachedNodes =
      useCache && cachedAllNodesData !== null && cachedAllNodesData !== undefined;
    setArePreviewNodesLoaded(hasCachedNodes);
  }, [useCache, cachedAllNodesData, partId, revisionId]);

  // Show single unified loader if anything is loading
  if ((useCache && !cachedRevisionData) || isAnyLoading) {
    return <InlineLoader text="Loading preview..." height="400px" />;
  }

  return (
    <div style={{ paddingBottom: "20px" }}>
      {/* Preview App Bar - Simplified version with close and export buttons */}
      <PreviewAppBar
        type={!fromAuditTrail ? finalCurrentRevision?.type_name : cancel_screen_data?.item_code_type}
        partNumber={!fromAuditTrail ? finalCurrentRevision?.part_number : cancel_screen_data?.partNumber}
        revision={!fromAuditTrail ? finalCurrentRevision?.rev : cancel_screen_data?.revision}
        revisionId={revisionId}
        partId={partId}
        description={!fromAuditTrail ? finalCurrentRevision?.part_description : cancel_screen_data?.partDescription}
        onExportClick={() => {
          if (!arePreviewNodesLoaded && !fromAuditTrail) {
            dispatch(
              showSnackbar({
                message: "Export will be enabled once all nodes are loaded.",
                type: "info",
              }),
            );
            return;
          }
          // Close additional information container before opening export popup
          setShowAdditionalInfo(false);
          setIsExportPopupOpen(true);
        }}
        onClose={handleClose}
      />

      {/* Exportable Content */}
      <div ref={contentRef}>
        {/* Document Header - Same as Document page */}
        <DocumentHeader
          type={!fromAuditTrail ? finalCurrentRevision?.type_name : cancel_screen_data?.item_code_type} 
          partNumber={!fromAuditTrail ? finalCurrentRevision?.part_number : cancel_screen_data?.partNumber} 
          revision={!fromAuditTrail ? finalCurrentRevision?.rev : cancel_screen_data?.revision} 
          revisionId={revisionId}
          partId={partId}
          description={!fromAuditTrail ? finalCurrentRevision?.part_description : cancel_screen_data?.partDescription} 
          status={!fromAuditTrail ? currentStatus : cancel_screen_data?.status} 
          currentRevision={!fromAuditTrail ? finalCurrentRevision : cancel_screen_data?.data}
          isCheckboxEnabled={false}
          showCheckbox={false}
          showAdditionalInfo={showAdditionalInfo}
          onShowAdditionalInfoChange={setShowAdditionalInfo}
        />

        {/* Revisions Table - Read-only version without Create button */}
        <div style={{ marginTop: "30px", marginBottom: SECTION_TO_SECTION_GAP }}>
          <h3 style={{ margin: 0 }}>Revisions Table</h3>
          <RevisionTable
            revisionData={revisionData || []}
            revisionId={revisionId}
            showHeading={false}
            tableMarginTop={HEADING_TO_TABLE_GAP}
            tableMarginBottom={0}
            showCreateButton={false}
            isReadOnly={true}
            partId={partId}
            partNumber={finalCurrentRevision?.part_number}
            type={finalCurrentRevision?.type_name}
            description={finalCurrentRevision?.part_description}
            myRevision={finalCurrentRevision?.rev}
          />
        </div>

        {/* Training Log Table */}
        <div style={{ marginBottom: SECTION_TO_SECTION_GAP }}>
          <h3 style={{ margin: 0 }}>Training Log</h3>
          <TrainingLogTable
            trainingLogs={trainingLogs || []}
            showHeading={false}
            tableMarginTop={HEADING_TO_TABLE_GAP}
            tableMarginBottom={0}
          />
        </div>

        {/* BOM Table */}
        <BOMTable
          type={finalCurrentRevision?.type_name}
          bomTable={bomTable || []}
        />

        {/* Revision Nodes - Read-only (all checkboxes hidden) */}
        <RenderRevisionNodes
          partRef={partId}
          procedureRevRef={revisionId}
          partNumber={finalCurrentRevision?.part_number}
          revision={finalCurrentRevision?.rev}
          disabled={true}
          showCheckbox={false}
          preloadedData={renderRevisionNodesData}
          onDataLoaded={() => setArePreviewNodesLoaded(true)}
        />
      </div>

      {/* Export Format Popup - Legacy from old preview */}
      <ExportFormatPopup
        isOpen={isExportPopupOpen}
        onClose={() => setIsExportPopupOpen(false)}
        bomTable={bomTable || []}
        revisionData={revisionData || []}
        trainingLogs={trainingLogs || []}
        partId={partId}
        type={finalCurrentRevision?.type_name || ""}
        partNumber={finalCurrentRevision?.part_number || ""}
        myRevision={finalCurrentRevision?.rev || ""}
        revisionId={revisionId}
        description={finalCurrentRevision?.part_description || ""}
        contentRef={contentRef}
        appSetupItems={appSetupItems}
        status={currentStatus}
      />
    </div>
  );
}

export default Preview;
